import { db } from '../auth/firebaseConfig';
import {
  doc, setDoc, collection, getDocs, query, where, orderBy
} from 'firebase/firestore';

const ANALYTICS_COLLECTION = 'ubunited_analytics';

function getDeviceType() {
  const w = window.innerWidth;
  if (w <= 768) return 'mobile';
  if (w <= 1024) return 'tablet';
  return 'desktop';
}

function getBrowser() {
  const ua = navigator.userAgent;
  if (ua.includes('Chrome') && !ua.includes('Edg')) return 'Chrome';
  if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Edg')) return 'Edge';
  return 'Other';
}

function getSessionId() {
  let sid = sessionStorage.getItem('uu_sid');
  if (!sid) {
    sid = Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
    sessionStorage.setItem('uu_sid', sid);
  }
  return sid;
}

function isReturningVisitor() {
  const key = 'uu_visited';
  const visited = localStorage.getItem(key);
  if (!visited) {
    localStorage.setItem(key, '1');
    return false;
  }
  return true;
}

export function logPageView(pathname) {
  // Fire-and-forget — don't await, don't block navigation
  const now = new Date();
  const docId = `${getSessionId()}_${Date.now()}`;
  setDoc(doc(db, ANALYTICS_COLLECTION, docId), {
    page: pathname,
    device: getDeviceType(),
    browser: getBrowser(),
    screenWidth: window.innerWidth,
    referrer: document.referrer || '',
    sessionId: getSessionId(),
    returning: isReturningVisitor(),
    timestamp: now.toISOString(),
    date: now.toISOString().split('T')[0],
    hour: now.getHours(),
  }).catch(() => {});
}

export async function getAnalytics(days = 30) {
  const since = new Date();
  since.setDate(since.getDate() - days);
  const sinceStr = since.toISOString().split('T')[0];

  // Also fetch previous period for comparison
  const prevSince = new Date();
  prevSince.setDate(prevSince.getDate() - (days * 2));
  const prevSinceStr = prevSince.toISOString().split('T')[0];

  const q = query(
    collection(db, ANALYTICS_COLLECTION),
    where('date', '>=', prevSinceStr),
    orderBy('date', 'desc')
  );

  const snapshot = await getDocs(q);
  const allDocs = snapshot.docs.map(d => d.data());

  // Split into current and previous period
  const currentDocs = allDocs.filter(d => d.date >= sinceStr);
  const prevDocs = allDocs.filter(d => d.date >= prevSinceStr && d.date < sinceStr);

  const aggregate = (docs) => {
    const sessions = new Set();
    const sessionPages = {};
    const pageViews = {};
    const devices = { mobile: 0, tablet: 0, desktop: 0 };
    const browsers = {};
    const dailyViews = {};
    const dailySessions = {};
    const hourlyViews = new Array(24).fill(0);
    const referrers = {};
    let newVisitors = 0;
    let returningVisitors = 0;
    const sessionFirstSeen = {};

    docs.forEach(d => {
      sessions.add(d.sessionId);

      if (d.sessionId) {
        sessionPages[d.sessionId] = (sessionPages[d.sessionId] || 0) + 1;
        if (!sessionFirstSeen[d.sessionId]) {
          sessionFirstSeen[d.sessionId] = true;
          if (d.returning) returningVisitors++;
          else newVisitors++;
        }
      }

      pageViews[d.page] = (pageViews[d.page] || 0) + 1;
      if (d.device) devices[d.device] = (devices[d.device] || 0) + 1;
      if (d.browser) browsers[d.browser] = (browsers[d.browser] || 0) + 1;

      if (d.date) {
        dailyViews[d.date] = (dailyViews[d.date] || 0) + 1;
        if (!dailySessions[d.date]) dailySessions[d.date] = new Set();
        dailySessions[d.date].add(d.sessionId);
      }

      if (typeof d.hour === 'number') hourlyViews[d.hour]++;

      if (d.referrer) {
        try {
          const host = new URL(d.referrer).hostname;
          if (host && !host.includes('ubunited') && !host.includes('localhost')) {
            referrers[host] = (referrers[host] || 0) + 1;
          }
        } catch (e) { /* invalid url */ }
      }
    });

    // Bounce rate: sessions with only 1 page view
    const sessionCounts = Object.values(sessionPages);
    const bouncedSessions = sessionCounts.filter(c => c === 1).length;
    const bounceRate = sessionCounts.length > 0 ? (bouncedSessions / sessionCounts.length) * 100 : 0;
    const avgPagesPerSession = sessionCounts.length > 0
      ? sessionCounts.reduce((a, b) => a + b, 0) / sessionCounts.length : 0;

    const topPages = Object.entries(pageViews).sort((a, b) => b[1] - a[1]).slice(0, 10);
    const topBrowsers = Object.entries(browsers).sort((a, b) => b[1] - a[1]);
    const topReferrers = Object.entries(referrers).sort((a, b) => b[1] - a[1]).slice(0, 5);

    const dailySorted = Object.entries(dailyViews)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([date, views]) => ({
        date,
        views,
        sessions: dailySessions[date]?.size || 0,
      }));

    const peakHour = hourlyViews.indexOf(Math.max(...hourlyViews));

    return {
      totalPageViews: docs.length,
      uniqueSessions: sessions.size,
      avgPagesPerSession,
      bounceRate,
      newVisitors,
      returningVisitors,
      topPages,
      devices,
      topBrowsers,
      topReferrers,
      dailyViews: dailySorted,
      hourlyViews,
      peakHour,
    };
  };

  const current = aggregate(currentDocs);
  const previous = aggregate(prevDocs);

  // Today vs yesterday
  const today = new Date().toISOString().split('T')[0];
  const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
  const todayData = current.dailyViews.find(d => d.date === today);
  const yesterdayData = current.dailyViews.find(d => d.date === yesterday);

  return {
    ...current,
    previous: {
      totalPageViews: previous.totalPageViews,
      uniqueSessions: previous.uniqueSessions,
      avgPagesPerSession: previous.avgPagesPerSession,
      bounceRate: previous.bounceRate,
    },
    todayViews: todayData?.views || 0,
    todaySessions: todayData?.sessions || 0,
    yesterdayViews: yesterdayData?.views || 0,
    days,
  };
}
